import React from 'react';
import { connect } from 'react-redux';
import { Button, Input, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { Edit16Regular, Open16Regular } from '@fluentui/react-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// functions
import * as ComponentFunctions from './PractitionerConsumptionDetailsMini.Functions';
import { ProtocolConsumptionRealizeTypeEnum } from '../../../../models/Enums/ProtocolConsumptionRealizeTypeEnum';
import { PatientVisitStatusEnum } from '../../../../models/Enums/PatientVisitStatusEnum';
import { formatCurrencyAmount } from '../PractitionerConsumptionCurrency';
import { conversionOf, feeBreakdown, formatPercentage, formatRate, hasFeeBreakdown, rateTypeLabel } from '../../../../commons/fee/PractitionerFee.Logic';
import { FeeDraft, amountDraftFromSource, canEditFee, deriveFee, draftFromSource, hasFeeErrors, isAmountDraftDirty, isFeeDraftDirty, parseAmount, unitAmountFor, validateAmountDraft, validateFeeDraft } from '../../../../commons/fee/FeeEditor.Logic';
import { LockClosed16Regular } from '@fluentui/react-icons';
import PractitionerConsumptionRateDrawerComponent from '../../PractitionerConsumptionRateDrawer/PractitionerConsumptionRateDrawer.Component';

import './PractitionerConsumptionDetailsMini.Styles.css';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid, GuideHelpText } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

const REALIZE_LABELS: Record<number, string> = {
  [ProtocolConsumptionRealizeTypeEnum.RealizedRemotely]: 'Remota',
  [ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace]: 'Presencial',
  [ProtocolConsumptionRealizeTypeEnum.RealizedAtHome]: 'Domicilio',
  [ProtocolConsumptionRealizeTypeEnum.NotRealized]: 'No realizada',
};

const VISIT_STATUS: Record<number, { label: string; tone: string }> = {
  [PatientVisitStatusEnum.Started]: { label: 'Iniciada', tone: 'info' },
  [PatientVisitStatusEnum.Completed]: { label: 'Completada', tone: 'success' },
  [PatientVisitStatusEnum.Skipped]: { label: 'Salteada', tone: 'danger' },
  [PatientVisitStatusEnum.Failed]: { label: 'Falla de selección', tone: 'danger' },
  [PatientVisitStatusEnum.Changed]: { label: 'Modificada', tone: 'warning' },
  [PatientVisitStatusEnum.AutoCompleted]: { label: 'Autocompletada', tone: 'success' },
};

/** Estado de liquidación del consumo → tono de etiqueta de la guía. */
const STATUS_TONE: Record<string, string> = {
  pending: 'warning', partial: 'warning', paid: 'success', settled: 'info', rejected: 'danger',
  ignored: 'muted', tosettle: 'info', invoiced: 'confirmed', unknown: 'muted',
};

const fmtDate = (value: string | Date | undefined | null, withTime: boolean = true) =>
  value ? moment(value).format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : '—';

/**
 * Detalle de un consumo del médico. Guía de diseño: cabecera con etiquetas de
 * estado, secciones con título en mayúsculas de 12, datos en pares
 * etiqueta/valor, el honorario como importe destacado y el cálculo como
 * cascada contable.
 */
const PractitionerConsumptionDetailsMiniComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const c = props.Consumption;

  const [isRateDrawerOpen, setIsRateDrawerOpen] = React.useState(false);

  // Edición del cálculo del honorario: los valores se editan, el honorario se deriva.
  const [isFeeEditorVisible, setIsFeeEditorVisible] = React.useState(false);
  const [feeDraft, setFeeDraft] = React.useState<FeeDraft>({ source: '', withholding: '', discountedCost: '', percentage: '' });
  React.useEffect(() => {
    if (c) setFeeDraft(draftFromSource(c));
    setIsFeeEditorVisible(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id, c?.amount, c?.percentageApplied, c?.sourceConsumptionAmount, c?.sourceWithholdingAmount, c?.discountedCostAmount, props.isOpen]);

  const feeErrors = validateFeeDraft(feeDraft);
  const derived = deriveFee(feeDraft);
  const feeEditable = canEditFee(c?.status);
  const feeDirty = c ? isFeeDraftDirty(feeDraft, c) : false;
  const canSaveFee = feeEditable && !hasFeeErrors(feeErrors) && feeDirty && !props.isSaving;

  const onSaveFee = () => {
    if (!c || !canSaveFee) return;
    const practitionerId = props.PractitionerId || c.practitionerId;
    if (!practitionerId) return;
    // La API sólo persiste el monto: se manda el honorario derivado, repartido por unidad.
    props.ChangeAmount(practitionerId, c.id, unitAmountFor(derived.fee, c.quantity), c.currencyCode || 'USD');
    setIsFeeEditorVisible(false);
  };

  // Honorario sin base de cálculo (valor fijo, lista de precios, consumo manual): no hay de
  // dónde derivarlo, así que el importe se edita directo. Mismo guardado: por unidad.
  const [isAmountEditorVisible, setIsAmountEditorVisible] = React.useState(false);
  const [amountDraft, setAmountDraft] = React.useState<string>('');
  React.useEffect(() => {
    setAmountDraft(c ? amountDraftFromSource(c) : '');
    setIsAmountEditorVisible(false);
  }, [c?.id, c?.amount, props.isOpen]);
  const amountError = validateAmountDraft(amountDraft);
  const amountDirty = c ? isAmountDraftDirty(amountDraft, c) : false;
  const canSaveAmount = feeEditable && !amountError && amountDirty && !props.isSaving;
  const amountUnit = c && !amountError ? unitAmountFor(parseAmount(amountDraft), c.quantity) : 0;

  const onSaveAmount = () => {
    if (!c || !canSaveAmount) return;
    const practitionerId = props.PractitionerId || c.practitionerId;
    if (!practitionerId) return;
    props.ChangeAmount(practitionerId, c.id, amountUnit, c.currencyCode || 'USD');
    setIsAmountEditorVisible(false);
  };

  const setDraftField = (key: keyof FeeDraft) => (_event: React.ChangeEvent<HTMLInputElement>, data: { value: string }) =>
    setFeeDraft((current) => ({ ...current, [key]: data.value }));
  const selectOnClick = (event: React.MouseEvent<HTMLInputElement>) => event.currentTarget.select();

  const fmt = (amount: number | undefined | null) => formatCurrencyAmount(amount ?? 0, c?.currencyCode);

  const status = c ? ComponentFunctions.GetStatusDescription(c.status) : undefined;
  const visit = c && c.visitStatus !== undefined ? VISIT_STATUS[c.visitStatus] : undefined;
  const modality = c ? REALIZE_LABELS[c.realizedType] : undefined;
  const warning = c?.markedAsHaveWarnings ? ((c.warningsComments || '').trim() || 'Este consumo tiene una advertencia.') : '';
  const breakdown = c && hasFeeBreakdown(c) ? feeBreakdown(c) : undefined;
  const conversion = conversionOf(c);
  const conversionType = conversion ? rateTypeLabel(conversion.rateType) : undefined;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="large"
      eyebrow="Consumo del médico"
      title={c?.serviceName || 'Detalle de consumo'}
      subtitle={c && (status || modality || visit) ? (
        <span className="pcd-tags">
          {status && <span className={`pcd-tag pcd-tag--${STATUS_TONE[status.class] || 'muted'}`}>{status.text}</span>}
          {modality && <span className="pcd-tag pcd-tag--muted">{modality}</span>}
          {visit && <span className={`pcd-tag pcd-tag--${visit.tone}`} title="Estado de la visita en la historia clínica">HC: {visit.label}</span>}
        </span>
      ) : undefined}
      onDismiss={props.onClose}
      closeLabel="Cerrar"
      className="pcd-drawer"
      testId="practitioner-consumption-details-drawer"
      secondaryAction={{ label: t('consumptionRateWorkflow.close', 'Cerrar') }}
    >
        {props.isLoading && (
          <div className="pcd-muted pcd-loading"><Spinner size="extra-tiny" /> Cargando detalle…</div>
        )}

        {c && !props.isLoading && (
          <>
            {warning && (
              <GuideMessageBar intent="warning">
                <MessageBarBody>
                  <MessageBarTitle>Advertencia</MessageBarTitle>
                  {warning}
                </MessageBarBody>
              </GuideMessageBar>
            )}

            {/* ── Visita ── */}
            <section className="pcd-section">
              <h3 className="pcd-section__title">Visita</h3>
              <dl className="pcd-grid">
                <div className="pcd-item">
                  <dt>Protocolo</dt>
                  <dd>{c.protocolName || '—'}</dd>
                </div>
                <div className="pcd-item">
                  <dt>Paciente</dt>
                  <dd>
                    {c.patientNumberInProtocol || '—'}
                    {c.medicalRecordNumber && <span className="pcd-meta"> · HC {c.medicalRecordNumber}</span>}
                  </dd>
                </div>
                <div className="pcd-item">
                  <dt>Etapa</dt>
                  <dd>{c.stageName || 'Sin visita asociada'}</dd>
                </div>
                <div className="pcd-item">
                  <dt>Fecha de realización</dt>
                  <dd>{fmtDate(c.serviceDueDate)}</dd>
                </div>
                {c.conceptDescription && (
                  <div className="pcd-item pcd-item--wide">
                    <dt>Concepto</dt>
                    <dd>{c.conceptDescription}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/* ── Liquidaciones: la del sponsor (de donde sale el consumo) y la del médico (donde se paga este honorario) ── */}
            <section className="pcd-section">
              <h3 className="pcd-section__title">Liquidaciones</h3>
              <div className="pcd-settlements">
                <div className="pcd-settlement">
                  <span className="pcd-tag pcd-tag--info">Sponsor</span>
                  <div className="pcd-settlement__body">
                    <span className="pcd-settlement__label">Liquidación protocolar donde se facturó la visita</span>
                    {c.relatedSettlementId ? (
                      <a className="pcd-link" href={'../settlements/' + c.relatedSettlementId} target="_blank" rel="noreferrer">
                        <Open16Regular /> {c.relatedSettlementName || c.relatedSettlementIdentifier || `LIQ-${c.relatedSettlementId}`}
                      </a>
                    ) : <span className="pcd-muted">Todavía no se liquidó al sponsor</span>}
                  </div>
                </div>
                <div className="pcd-settlement">
                  <span className="pcd-tag pcd-tag--confirmed">Médico</span>
                  <div className="pcd-settlement__body">
                    <span className="pcd-settlement__label">Liquidación del médico donde se paga este honorario</span>
                    {c.settlementId ? (
                      <a className="pcd-link" href={'../settlements/' + c.settlementId} target="_blank" rel="noreferrer">
                        <Open16Regular /> {c.settlementName || c.settlementIdentifier || `LIQ-${c.settlementId}`}
                      </a>
                    ) : <span className="pcd-muted">Todavía no está en una liquidación del médico</span>}
                  </div>
                </div>
              </div>
            </section>

            {/* ── Honorario ── */}
            <section className="pcd-section">
              <div className="pcd-section__head">
                <h3 className="pcd-section__title">Honorario</h3>
                {/* Con base de cálculo el honorario no se edita suelto: sólo desde "Cómo se calcula",
                    para que quede coherente con la base. Sin base (valor fijo, lista de precios,
                    consumo manual) se edita el importe directo. Un consumo que ya no está "sin
                    liquidar" es inmutable. */}
                {!feeEditable && (
                  <span className="pcd-lock" title="Sólo se puede modificar un consumo sin liquidar.">
                    <LockClosed16Regular /> Inmutable
                  </span>
                )}
                {feeEditable && !breakdown && (
                  <Button appearance="subtle" size="small" icon={<Edit16Regular />} onClick={() => { setIsAmountEditorVisible((v) => !v); if (c) setAmountDraft(amountDraftFromSource(c)); }}>
                    {isAmountEditorVisible ? 'Cancelar edición' : 'Editar importe'}
                  </Button>
                )}
              </div>

              {!isAmountEditorVisible && (
                <div className="pcd-amount">
                  <span className="pcd-amount__label">Total</span>
                  <span className="pcd-amount__value">{fmt(c.amount)}</span>
                  {conversion && (
                    <span className="pcd-amount__conv" title={conversionType ? `Convertido con ${conversionType}` : 'Convertido'}>
                      desde {formatCurrencyAmount(conversion.originalAmount, conversion.originalCurrency)} × {formatRate(conversion.rate)}
                    </span>
                  )}
                </div>
              )}

              {isAmountEditorVisible && (
                <div className="pcd-editor" data-testid="amount-editor">
                  <GuideFieldGrid>
                    <GuideField label="Honorario" error={amountError} hint={amountError ? undefined : 'Importe total del consumo.'}>
                      <Input type="number" step="0.01" min="0" value={amountDraft} onChange={(_e, data) => setAmountDraft(data.value)} onClick={selectOnClick} />
                    </GuideField>
                  </GuideFieldGrid>
                  <table className="pcd-calc">
                    <tbody>
                      <tr><td>Cantidad</td><td className="pcd-calc__amount">{c.quantity ?? 1}</td></tr>
                      <tr className="pcd-calc__result">
                        <td>Unitario resultante</td>
                        <td className="pcd-calc__amount">
                          {fmt(amountUnit)}
                          {amountDirty && <span className="pcd-calc__was"> (antes {fmt(c.unitAmount)})</span>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <GuideHelpText>Este honorario no sale de una base de cálculo: se guarda el importe tal cual lo cargues, repartido por unidad.</GuideHelpText>
                  <div className="pcd-editor__actions">
                    <Button appearance="secondary" onClick={() => { setIsAmountEditorVisible(false); if (c) setAmountDraft(amountDraftFromSource(c)); }}>Cancelar</Button>
                    <Button appearance="primary" disabled={!canSaveAmount} onClick={onSaveAmount} icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}>
                      Guardar honorario
                    </Button>
                  </div>
                </div>
              )}

              <div className="pcd-strip">
                <div className="pcd-strip__item">
                  <span className="pcd-strip__label">Unitario</span>
                  <span className="pcd-strip__value">{fmt(c.unitAmount)}</span>
                </div>
                <div className="pcd-strip__item">
                  <span className="pcd-strip__label">Cantidad</span>
                  <span className="pcd-strip__value">{c.quantity ?? 1}</span>
                </div>
                <div className="pcd-strip__item">
                  <span className="pcd-strip__label">Pagado</span>
                  <span className="pcd-strip__value">{fmt(c.payeeAmount)}</span>
                </div>
                <div className="pcd-strip__item">
                  <span className="pcd-strip__label">Pendiente</span>
                  <span className={`pcd-strip__value${(c.pendingAmount ?? 0) > 0 ? ' pcd-strip__value--pending' : ''}`}>{fmt(c.pendingAmount)}</span>
                </div>
              </div>

            </section>

            {/* ── Cómo se calcula ── */}
            {breakdown && (
              <section className="pcd-section">
                <div className="pcd-section__head">
                  <h3 className="pcd-section__title">Cómo se calcula el honorario</h3>
                  {feeEditable && (
                    <Button appearance="subtle" size="small" icon={<Edit16Regular />} onClick={() => { setIsFeeEditorVisible((v) => !v); if (c) setFeeDraft(draftFromSource(c)); }}>
                      {isFeeEditorVisible ? 'Cancelar edición' : 'Editar cálculo'}
                    </Button>
                  )}
                </div>

                {!isFeeEditorVisible && (
                  <table className="pcd-calc">
                    <tbody>
                      <tr><td>Facturado al sponsor</td><td className="pcd-calc__amount">{fmt(breakdown.source)}</td></tr>
                      <tr className="pcd-calc__deduct"><td>− Retención</td><td className="pcd-calc__amount">− {fmt(breakdown.withholding)}</td></tr>
                      <tr className="pcd-calc__deduct"><td>− Costo descontado</td><td className="pcd-calc__amount">− {fmt(breakdown.discountedCost)}</td></tr>
                      <tr className="pcd-calc__base"><td>Base de cálculo</td><td className="pcd-calc__amount">{fmt(breakdown.base)}</td></tr>
                      <tr><td>× Porcentaje pactado</td><td className="pcd-calc__amount">{formatPercentage(breakdown.percentage)}</td></tr>
                      <tr className="pcd-calc__result"><td>Honorario</td><td className="pcd-calc__amount">{fmt(breakdown.fee)}</td></tr>
                    </tbody>
                  </table>
                )}

                {/* Editor: se cargan los valores y el honorario se recalcula solo; nunca se escribe a mano ni baja de cero */}
                {isFeeEditorVisible && (
                  <div className="pcd-editor">
                    <GuideFieldGrid>
                      <GuideField label="Facturado al sponsor" error={feeErrors.source}>
                        <Input type="number" step="0.01" min="0" value={feeDraft.source} onChange={setDraftField('source')} onClick={selectOnClick} />
                      </GuideField>
                      <GuideField label="Retención" error={feeErrors.withholding}>
                        <Input type="number" step="0.01" min="0" value={feeDraft.withholding} onChange={setDraftField('withholding')} onClick={selectOnClick} />
                      </GuideField>
                      <GuideField label="Costo descontado" error={feeErrors.discountedCost}>
                        <Input type="number" step="0.01" min="0" value={feeDraft.discountedCost} onChange={setDraftField('discountedCost')} onClick={selectOnClick} />
                      </GuideField>
                      <GuideField label="Porcentaje pactado" error={feeErrors.percentage} hint={feeErrors.percentage ? undefined : 'De 0 a 100.'}>
                        <Input type="number" step="0.01" min="0" max="100" value={feeDraft.percentage} onChange={setDraftField('percentage')} onClick={selectOnClick} />
                      </GuideField>
                    </GuideFieldGrid>
                    {feeErrors.deductions && (
                      <GuideMessageBar intent="warning"><MessageBarBody>{feeErrors.deductions}</MessageBarBody></GuideMessageBar>
                    )}
                    <table className="pcd-calc">
                      <tbody>
                        <tr className="pcd-calc__base"><td>Base de cálculo</td><td className="pcd-calc__amount">{fmt(derived.base)}</td></tr>
                        <tr className="pcd-calc__result">
                          <td>Honorario resultante</td>
                          <td className="pcd-calc__amount">
                            {fmt(derived.fee)}
                            {feeDirty && Math.abs(derived.fee - breakdown.fee) >= 0.01 && (
                              <span className="pcd-calc__was"> (antes {fmt(breakdown.fee)})</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <GuideHelpText>El honorario sale de estos valores y nunca es menor que cero. Lo que se guarda es el honorario resultante.</GuideHelpText>
                    <div className="pcd-editor__actions">
                      <Button appearance="secondary" onClick={() => { setIsFeeEditorVisible(false); if (c) setFeeDraft(draftFromSource(c)); }}>Cancelar</Button>
                      <Button appearance="primary" disabled={!canSaveFee} onClick={onSaveFee} icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}>
                        Guardar honorario
                      </Button>
                    </div>
                  </div>
                )}

                {!isFeeEditorVisible && breakdown.hasManualAdjustment && (
                  <GuideMessageBar intent="warning">
                    <MessageBarBody>El honorario no coincide con base × porcentaje: tiene un ajuste manual o una cotización aplicada.</MessageBarBody>
                  </GuideMessageBar>
                )}
              </section>
            )}

            {/* ── Cotización ── */}
            <section className="pcd-section">
              <div className="pcd-section__head">
                <h3 className="pcd-section__title">{t('consumptionRateWorkflow.currentRate')}</h3>
                <Button appearance="subtle" size="small" icon={<Edit16Regular />} onClick={() => setIsRateDrawerOpen(true)}>
                  {t('consumptionRateWorkflow.editRate', 'Modificar cotización')}
                </Button>
              </div>
              <dl className="pcd-grid">
                <div className="pcd-item">
                  <dt>{t('conversionSnapshot.rate')}</dt>
                  <dd>{c.rate ? formatRate(c.rate) : '—'}</dd>
                </div>
                <div className="pcd-item">
                  <dt>{t('conversionSnapshot.rateType')}</dt>
                  <dd>{ComponentFunctions.getRateTypeDescription(c.rateType)}</dd>
                </div>
                <div className="pcd-item">
                  <dt>{t('conversionSnapshot.originalCurrencyCode')}</dt>
                  <dd>{c.originalCurrencyCode ? c.originalCurrencyCode.toUpperCase() : '—'}</dd>
                </div>
                <div className="pcd-item">
                  <dt>{t('conversionSnapshot.originalUnitAmount')}</dt>
                  <dd>{c.originalCurrencyCode ? formatCurrencyAmount(c.originalUnitAmount ?? 0, c.originalCurrencyCode) : '—'}</dd>
                </div>
                <div className="pcd-item">
                  <dt>{t('conversionSnapshot.originalAmount')}</dt>
                  <dd>{c.originalCurrencyCode ? formatCurrencyAmount(c.originalAmount ?? 0, c.originalCurrencyCode) : '—'}</dd>
                </div>
                <div className="pcd-item">
                  <dt>Liquidación del sponsor</dt>
                  <dd>{c.relatedSettlementName || c.relatedSettlementIdentifier || (c.relatedSettlementId ? `LIQ-${c.relatedSettlementId}` : '—')}</dd>
                </div>
              </dl>
            </section>

            {/* ── Comentarios ── */}
            {(c.comments || c.moreInfoComments || c.troubleComments) && (
              <section className="pcd-section">
                <h3 className="pcd-section__title">Comentarios</h3>
                {c.comments && <p className="pcd-text">{c.comments}</p>}
                {c.moreInfoComments && <p className="pcd-text"><span className="pcd-meta">Falta información: </span>{c.moreInfoComments}</p>}
                {c.troubleComments && <p className="pcd-text"><span className="pcd-meta">Problema: </span>{c.troubleComments}</p>}
              </section>
            )}
          </>
        )}

      {c && props.PractitionerId && (
        <PractitionerConsumptionRateDrawerComponent
          PractitionerId={props.PractitionerId}
          ConsumptionId={c.id}
          isOpen={isRateDrawerOpen}
          onClose={() => setIsRateDrawerOpen(false)}
        />
      )}
    </GuideDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PractitionerConsumptionDetailsMiniComponent as any);
